// src/services/planService.js - SECURED
import { API_BASE_URL } from '../utils/constants';


const getAuthHeaders = () => {
  return {
    'Content-Type': 'application/json',
  };
};

const handleResponse = async (response) => {
  if (!response.ok) {
    if (response.status === 401) {
      window.location.href = '/';
    }
    const error = await response.json();
    throw new Error(error.error || error.detail || 'Request failed');
  }
  return response.json();
};

export const planService = {
  // Check if user can create a plan
  async checkPlanEligibility() {
    try {
      const response = await fetch(`${API_BASE_URL}/plan/check-eligibility/`, {
        method: 'GET',
        headers: getAuthHeaders(),
        credentials: 'include',
      });
      return handleResponse(response);
    } catch (error) {
      console.error('Error checking plan eligibility:', error);
      throw error;
    }
  },

  // Submit plan questionnaire
  async submitQuestionnaire(questionnaireData) {
    try {
      const response = await fetch(`${API_BASE_URL}/plan/questionnaire/submit/`, {
        method: 'POST',
        headers: getAuthHeaders(),
        credentials: 'include',
        body: JSON.stringify(questionnaireData),
      });
      return handleResponse(response);
    } catch (error) {
      console.error('Error submitting questionnaire:', error);
      throw error;
    }
  },

  // Get user's questionnaires
  async getUserQuestionnaires() {
    try {
      const response = await fetch(`${API_BASE_URL}/plan/questionnaire/list/`, {
        method: 'GET',
        headers: getAuthHeaders(),
        credentials: 'include',
      });
      return handleResponse(response);
    } catch (error) {
      console.error('Error fetching questionnaires:', error);
      throw error;
    }
  },

  // Create plan after questionnaire
  async createPlan(planData) {
    try {
      const response = await fetch(`${API_BASE_URL}/plan/create/`, {
        method: 'POST',
        headers: getAuthHeaders(),
        credentials: 'include',
        body: JSON.stringify(planData),
      });
      return handleResponse(response);
    } catch (error) {
      console.error('Error creating plan:', error);
      throw error;
    }
  },

  // Get user's plans
  async getUserPlans(filters = {}) {
    try {
      const params = new URLSearchParams();
      if (filters.status) params.append('status', filters.status);
      if (filters.is_locked !== undefined) params.append('is_locked', filters.is_locked);
      
      const url = `${API_BASE_URL}/plan/list/${params.toString() ? '?' + params.toString() : ''}`;
      
      const response = await fetch(url, {
        method: 'GET',
        headers: getAuthHeaders(),
        credentials: 'include',
      });
      return handleResponse(response);
    } catch (error) {
      console.error('Error fetching plans:', error);
      throw error;
    }
  },

  // Get plan history (including completed plans)
  async getPlanHistory() {
    try {
      const response = await fetch(`${API_BASE_URL}/plan/history/`, {
        method: 'GET',
        headers: getAuthHeaders(),
        credentials: 'include',
      });
      return handleResponse(response);
    } catch (error) {
      console.error('Error fetching plan history:', error);
      throw error;
    }
  },

  // Get specific plan detail
  async getPlanDetail(planId) {
    try {
      const response = await fetch(`${API_BASE_URL}/plan/${planId}/`, {
        method: 'GET',
        headers: getAuthHeaders(),
        credentials: 'include',
      });
      return handleResponse(response);
    } catch (error) {
      console.error('Error fetching plan detail:', error);
      throw error;
    }
  },

  // Get plan summary
  async getPlanSummary() {
    try {
      const response = await fetch(`${API_BASE_URL}/plan/summary/`, {
        method: 'GET',
        headers: getAuthHeaders(),
        credentials: 'include',
      });
      return handleResponse(response);
    } catch (error) {
      console.error('Error fetching plan summary:', error);
      throw error;
    }
  },

  // Execute periodic contribution for a plan
  async executePeriodicBuy(planId) {
    try {
      const response = await fetch(`${API_BASE_URL}/plan/periodic-buy/`, {
        method: 'POST',
        headers: getAuthHeaders(),
        credentials: 'include',
        body: JSON.stringify({ plan_id: planId }),
      });
      return handleResponse(response);
    } catch (error) {
      console.error('Error executing periodic contribution:', error);
      throw error;
    }
  },

  // Execute manual contribution for ONE_TIME plans
  async executeManualContribution(planId, amount) {
    try {
      const response = await fetch(`${API_BASE_URL}/plan/${planId}/contribute/`, {
        method: 'POST',
        headers: getAuthHeaders(),
        credentials: 'include',
        body: JSON.stringify({ amount: parseFloat(amount) }),
      });
      return handleResponse(response);
    } catch (error) {
      console.error('Error executing manual contribution:', error);
      throw error;
    }
  },

  // Ignore reminder for ONE_TIME plans
  async ignoreReminder(planId) {
    try {
      const response = await fetch(`${API_BASE_URL}/plan/${planId}/ignore-reminder/`, {
        method: 'POST',
        headers: getAuthHeaders(),
        credentials: 'include',
      });
      return handleResponse(response);
    } catch (error) {
      console.error('Error ignoring reminder:', error);
      throw error;
    }
  },

  // Withdraw from plan
  async withdrawFromPlan(planId, amount) {
    try {
      const response = await fetch(`${API_BASE_URL}/plan/${planId}/withdraw/`, {
        method: 'POST',
        headers: getAuthHeaders(),
        credentials: 'include',
        body: JSON.stringify({ amount }),
      });
      return handleResponse(response);
    } catch (error) {
      console.error('Error withdrawing from plan:', error);
      throw error;
    }
  },

  // Get plan notifications
  async getNotifications(filters = {}) {
    try {
      const params = new URLSearchParams();
      if (filters.is_read !== undefined) params.append('is_read', filters.is_read);
      if (filters.type) params.append('type', filters.type);
      
      const url = `${API_BASE_URL}/plan/notifications/${params.toString() ? '?' + params.toString() : ''}`;
      
      const response = await fetch(url, {
        method: 'GET',
        headers: getAuthHeaders(),
        credentials: 'include',
      });
      return handleResponse(response);
    } catch (error) {
      console.error('Error fetching notifications:', error);
      throw error;
    }
  },

  // Mark notification as read
  async markNotificationRead(notificationId) {
    try {
      const response = await fetch(`${API_BASE_URL}/plan/notifications/${notificationId}/read/`, {
        method: 'POST',
        headers: getAuthHeaders(),
        credentials: 'include',
      });
      return handleResponse(response);
    } catch (error) {
      console.error('Error marking notification as read:', error);
      throw error;
    }
  },

  // Mark all notifications as read
  async markAllNotificationsRead() {
    try {
      const response = await fetch(`${API_BASE_URL}/plan/notifications/read-all/`, {
        method: 'POST',
        headers: getAuthHeaders(),
        credentials: 'include',
      });
      return handleResponse(response);
    } catch (error) {
      console.error('Error marking all notifications as read:', error);
      throw error;
    }
  }
};